import React, { useState, useEffect } from 'react';
import { Trophy, Gift, Star, ChevronRight, MessageCircle, Instagram, Zap, Snowflake, Crown, ShieldCheck, CheckCircle, Filter, Sparkles, Gem, X } from 'lucide-react';
import API_BASE_URL from '../apiConfig';

const Home = () => {
  const [snowflakes, setSnowflakes] = useState([]);
  const [currentWinner, setCurrentWinner] = useState(null);
  const [titleIndex, setTitleIndex] = useState(0);
  const [chevronSize, setChevronSize] = useState(window.innerWidth < 768 ? 12 : 14);
  const [showPopup, setShowPopup] = useState(false);
  
  // API Verileri
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE_URL}/get_data.php`)
      .then(res => res.json())
      .then(data => {
        setApiData(data);
        setLoading(false);
        // Popup kontrolü
        if (data.popup?.active) {
            setTimeout(() => setShowPopup(true), 1000);
        }
      })
      .catch(err => {
        console.error("Veri çekme hatası:", err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setChevronSize(window.innerWidth < 768 ? 12 : 14);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const titles = ["GÜVENİLİR", "KAZANÇLI", "SPONSOR", "GÜNCEL"];

  useEffect(() => {
    const interval = setInterval(() => {
      setTitleIndex((prev) => (prev + 1) % titles.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [titles.length]);

  // Canlı Bildirim Animasyonu (Notifications)
  useEffect(() => {
    if (!apiData?.notifications || apiData.notifications.length === 0) return;

    const interval = setInterval(() => {
      const randomWinner = apiData.notifications[Math.floor(Math.random() * apiData.notifications.length)];
      setCurrentWinner(randomWinner);
    }, 4000);

    return () => clearInterval(interval);
  }, [apiData]);

  useEffect(() => {
    const flakes = Array.from({ length: 50 }).map((_, i) => ({
      id: i,
      left: Math.random() * 100,
      animationDuration: Math.random() * 3 + 2 + 's',
      opacity: Math.random() * 0.7 + 0.3,
      size: Math.random() * 6 + 3 + 'px',
      blur: Math.random() < 0.3 ? '2px' : '0px'
    }));
    setSnowflakes(flakes);
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#09101a] text-white">Yükleniyor...</div>;

  const vipColors = [
    { color: "from-cyan-300 to-blue-500", ringColor: "border-cyan-300/50" },
    { color: "from-sky-300 to-indigo-500", ringColor: "border-sky-300/50" },
    { color: "from-teal-300 to-emerald-500", ringColor: "border-teal-300/50" },
    { color: "from-blue-300 to-violet-500", ringColor: "border-blue-300/50" },
  ];

  const defaultColor = { color: "from-sky-200 to-cyan-400", ringColor: "border-cyan-300/50" };

  const logoCards = apiData?.logoCards || [];
  const bettingSites = apiData?.bettingSites || [];
  const config = apiData || {};
  const winners = apiData?.winners || [];
  const popupSites = apiData?.popupSites || [];

  const sponsors = [
    ...logoCards.map((item, index) => ({
      id: `vip-${item.id}`,
      name: item.alt.replace(' Logo', ''),
      slogan: item.title,
      tag: item.badge,
      score: "9." + (9 - index),
      color: vipColors[index % vipColors.length].color,
      ringColor: vipColors[index % vipColors.length].ringColor,
      imgSrc: item.imgSrc,
      backgroundUrl: item.backgroundUrl,
      url: item.url,
      isVip: true
    })),
    ...bettingSites.map((item, index) => ({
      id: `site-${item.id}`,
      name: item.name,
      slogan: item.isTrusted ? "GÜVENİLİR BAHİS" : "SPONSOR",
      tag: item.isTrusted ? "ONAYLI" : "",
      score: "9." + (5 - index),
      color: defaultColor.color,
      ringColor: defaultColor.ringColor,
      imgSrc: item.imgSrc,
      backgroundUrl: null,
      url: item.url,
      isVip: false
    }))
  ];

  return (
    <div className="relative min-h-screen bg-[#09101a] text-white font-sans overflow-hidden selection:bg-cyan-500 selection:text-black">
      
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <img 
            src="/img/background.jpg" 
            alt="Background" 
            className="w-full h-full object-cover opacity-85"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-[#0d1626]/50 to-[#09101a]" />
        </div>
        
        {snowflakes.map((flake) => (
          <div
            key={flake.id}
            className="absolute top-[-20px] bg-white rounded-full animate-fall shadow-[0_0_5px_rgba(255,255,255,0.8)]"
            style={{
              left: `${flake.left}%`,
              width: flake.size,
              height: flake.size,
              opacity: flake.opacity,
              filter: `blur(${flake.blur})`,
              animation: `fall ${flake.animationDuration} linear infinite`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 w-full max-w-5xl mx-auto px-2 sm:px-4 py-8 pb-24">
        
        {/* Header Section */}
        <div className="text-center mb-10 px-2">
          <div className="relative w-full max-w-3xl mx-auto p-1 rounded-[2.5rem] bg-gradient-to-b from-cyan-300/30 via-white/5 to-transparent shadow-[0_0_60px_-10px_rgba(34,211,238,0.2)]">
            <div className="absolute -top-4 -right-4 animate-bounce duration-[3000ms]">
              <Snowflake className="text-cyan-200/40 w-12 h-12 rotate-12" />
            </div>
            <div className="absolute -bottom-2 -left-2 animate-pulse">
               <Sparkles className="text-blue-200/30 w-8 h-8 -rotate-12" />
            </div>
            
            <div className="relative flex flex-col items-center justify-between p-6 md:p-8 bg-[#0B1221]/90 backdrop-blur-2xl rounded-[2.3rem] border border-cyan-500/20 overflow-hidden">
              
              <div className="absolute inset-0 overflow-hidden rounded-[2.3rem]">
                <div className="absolute inset-0 bg-gradient-to-b from-[#0B1221]/50 via-transparent to-[#0B1221]/80"></div>
              </div>

              {/* Üst LED Süsleme */}
              <img src="/img/led.png" alt="" className="hidden md:block absolute top-[4px] left-1/2 -translate-x-1/2 w-full h-24 object-cover pointer-events-none z-50 opacity-100 mix-blend-screen" />

              <div className="relative z-10 w-full flex flex-col md:flex-row items-center justify-between gap-6">
              
              {/* Sol Taraf: Logo ve Ana Başlık */}
              <div className="relative z-10 flex items-center gap-5 w-full md:w-auto justify-center md:justify-start">
                <div className="relative group">
                  <div className="absolute inset-0 bg-cyan-400 blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500 rounded-full"></div>
                  <div className="relative w-16 h-16 bg-gradient-to-br from-cyan-950 to-slate-900 rounded-2xl border border-cyan-500/30 flex items-center justify-center shadow-2xl rotate-3 group-hover:rotate-0 transition-all duration-300">
                    <img src="/img/logo.png" alt="Logo" className="w-10 h-10 object-contain drop-shadow-[0_0_10px_rgba(34,211,238,0.5)]" />
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-cyan-400 rounded-full animate-ping"></div>
                  </div>
                </div>


                <div className="flex flex-col items-start">
                  <div className="h-10 overflow-hidden relative w-56 md:w-64">
                    <div 
                      className="transition-transform duration-700 cubic-bezier(0.34, 1.56, 0.64, 1) absolute top-0 left-0"
                      style={{ transform: `translateY(-${titleIndex * 40}px)` }}
                    >
                      {titles.map((title, i) => (
                        <div key={i} className="h-10 flex items-center">
                          <span className="text-3xl md:text-4xl font-black tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-100 to-cyan-300 drop-shadow-sm whitespace-nowrap filter drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]">
                            {title}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="h-[1px] w-8 bg-cyan-500/50"></div>
                    <span className="text-xs font-bold text-cyan-400 tracking-[0.4em] uppercase">{config.headerTitle || "SİTELER"}</span>
                  </div>
                </div>
              </div>

              {/* Sağ Taraf: Badge ve URL */}
              <div className="relative z-10 flex flex-col items-center md:items-end gap-2 w-full md:w-auto">
                
                <div className="flex items-center gap-0.5 mt-1">
                  {"kitanax.pro".split("").map((char, i) => (
                    <span 
                      key={i}
                      className="text-sm font-black text-cyan-400/80 hover:text-cyan-300 transition-colors cursor-default select-none"
                      style={{ 
                        textShadow: '0 0 10px rgba(34,211,238,0.3)',
                        animation: `pulse 3s infinite ${i * 100}ms`
                      }}
                    >
                      {char}
                    </span>
                  ))}
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sponsors Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-1 gap-y-16">
          {sponsors.map((item, index) => {
            const isVip = item.isVip;
            const borderImg = index < 3 ? `/img/border/${index + 1}.png` : '/img/border/4.png';
            
            return (
              <div 
                key={item.id}
                className="group relative h-full"
              >
                <div className="absolute -inset-2 z-0 pointer-events-none">
                  <img src="/img/kare.png" alt="" className="w-full h-full object-fill scale-x-95 scale-y-110" />
                </div>

                <div className="relative flex flex-col items-center p-2 md:p-4 rounded-[1.5rem] transition-all duration-500 transform h-full justify-between z-10 scale-90">
                  
                  <div className="relative z-10 w-full flex flex-col items-center">
                    <div className="relative w-[8rem] h-[8rem] md:w-[11rem] md:h-[11rem] flex items-center justify-center mb-1">
                      <div className="absolute inset-0 z-20 pointer-events-none">
                         <img src={borderImg} alt="" className="w-full h-full object-contain drop-shadow-[0_0_8px_rgba(6,182,212,0.5)] scale-110" />
                      </div>
                      <div className="w-[6rem] h-[6rem] md:w-[9rem] md:h-[9rem] rounded-full bg-black/20 flex items-center justify-center overflow-hidden z-10 transition-transform duration-300">
                        <img 
                          src={item.imgSrc} 
                          alt={item.name} 
                          className="w-full h-full object-contain p-1.5" 
                        />
                      </div>
                    </div>
                  
                    <div className="mb-1 h-10 md:h-14 flex items-center justify-center">
                      <img src={item.imgSrc} alt={item.name} className="h-full max-w-[160px] md:max-w-[200px] object-contain" />
                    </div>

                    <div className={`
                      inline-flex items-center justify-center max-w-[180px] text-center rounded-lg backdrop-blur-md transition-all duration-300 mb-3 relative overflow-hidden
                      ${isVip
                        ? 'bg-gradient-to-r from-amber-500/10 via-yellow-400/20 to-amber-500/10 border border-yellow-400/40 shadow-[0_0_15px_rgba(250,204,21,0.15)] group-hover:shadow-[0_0_20px_rgba(250,204,21,0.3)] group-hover:border-yellow-300/60'
                        : 'border border-white/10 bg-white/5 group-hover:bg-cyan-500/10 group-hover:border-cyan-400/40'}
                    `}>
                      {isVip && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]"></div>}
                      <p className={`text-[10px] md:text-xs leading-tight font-black tracking-widest uppercase relative z-10 px-3 py-1.5 ${isVip ? 'text-yellow-400 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]' : 'text-gray-400 group-hover:text-cyan-300 transition-colors'}`}>
                        {item.slogan}
                      </p>
                    </div>
                  </div>

                  <a 
                    href={item.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`
                      relative z-10 flex items-center justify-center gap-1 px-8 md:px-10 h-9 md:h-10 rounded-full shadow-lg transition-all active:scale-95 group/btn border cursor-pointer no-underline
                      ${isVip 
                        ? `bg-gradient-to-r ${item.color} border-white/20 text-white shadow-lg hover:shadow-cyan-500/30` 
                        : 'bg-gradient-to-r from-cyan-600 to-blue-600 border-cyan-400/30 text-white shadow-cyan-500/20 hover:from-cyan-500 hover:to-blue-500'}
                    `}
                  >
                    <span className="text-[11px] md:text-xs font-bold tracking-wide">GİRİŞ</span>
                    <div className={`p-0.5 rounded-full ${isVip ? 'bg-white/20' : 'bg-black/20'}`}>
                      <ChevronRight size={chevronSize} className="group-hover/btn:translate-x-0.5 transition-transform" />
                    </div>
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        {/* Social Links */}
        <div className="grid grid-cols-2 gap-3 my-8">
          <a href="https://t.me/+puvfb99vJ3YyYTA0" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 p-4 rounded-3xl bg-gradient-to-r from-[#0088cc]/10 to-[#0088cc]/5 border border-[#0088cc]/20 hover:border-[#0088cc]/50 hover:bg-[#0088cc]/20 transition-all group backdrop-blur-md no-underline">
            <div className="bg-[#0088cc]/20 p-2 rounded-full group-hover:scale-110 transition-transform">
               <MessageCircle size={20} className="text-[#00aaff]" />
            </div>
            <div className="text-left">
              <div className="text-xs font-bold text-cyan-100">Telegram</div>
              <div className="text-[10px] text-cyan-400/60">Kanalımıza Katıl</div>
            </div>
          </a>
          <a href="https://www.instagram.com/yeahmurbiliyorx/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 p-4 rounded-3xl bg-gradient-to-r from-[#E1306C]/10 to-[#E1306C]/5 border border-[#E1306C]/20 hover:border-[#E1306C]/50 hover:bg-[#E1306C]/20 transition-all group backdrop-blur-md no-underline">
            <div className="bg-[#E1306C]/20 p-2 rounded-full group-hover:scale-110 transition-transform">
              <Instagram size={20} className="text-[#ff4d88]" />
            </div>
            <div className="text-left">
              <div className="text-xs font-bold text-pink-100">Instagram</div>
              <div className="text-[10px] text-pink-400/60">Bizi Takip Et</div>
            </div>
          </a>
        </div>

        {/* Winners Section */}
        <div className="relative mt-8">
          <div className="flex items-center justify-between mb-4 px-2">
            <h2 className="text-amber-400 font-bold tracking-widest uppercase text-xs flex items-center gap-2">
              <Gem size={14} className="text-amber-300" /> Haftanın Kazananları
            </h2>
            <span className="text-[10px] text-gray-500 bg-white/5 px-2 py-1 rounded-full">Canlı Akış ●</span>
          </div>

          <div className="bg-[#0f1722]/60 backdrop-blur-md rounded-[2rem] border border-white/5 p-3 space-y-2">
            {winners.map((winner, idx) => (
              <div key={winner.id} className="flex items-center justify-between p-3 rounded-2xl hover:bg-white/5 transition-colors group border border-transparent hover:border-white/5">
                <div className="flex items-center gap-3">
                  <div className={`
                    w-9 h-9 flex items-center justify-center rounded-xl font-bold text-sm shadow-lg
                    ${idx === 0 ? 'bg-gradient-to-br from-amber-400 to-orange-600 text-white shadow-amber-500/20' : 
                      idx === 1 ? 'bg-gradient-to-br from-slate-300 to-slate-500 text-white shadow-slate-500/20' : 
                      idx === 2 ? 'bg-gradient-to-br from-orange-400 to-red-500 text-white shadow-orange-500/20' : 'text-gray-500 bg-white/5'}
                  `}>
                    {idx + 1}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-200">{winner.user}</div>
                    <div className="text-[10px] text-gray-500 flex items-center gap-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                      {winner.site}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`font-mono font-bold text-sm ${idx < 3 ? 'text-green-400 drop-shadow-[0_0_8px_rgba(74,222,128,0.3)]' : 'text-gray-500'}`}>
                    {winner.amount}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Live Notification */}
      {currentWinner && (
        <div key={currentWinner.id || Math.random()} className="fixed bottom-4 right-4 z-50 w-auto max-w-[300px] animate-slideUp">
           <div className="bg-[#131b29]/95 backdrop-blur-xl border border-green-500/20 px-4 py-3 rounded-2xl flex items-center justify-between gap-3 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.5)]">
              <div className="flex items-center gap-3">
                <div className="bg-green-500/20 p-2 rounded-xl">
                   <Sparkles size={16} className="text-green-400 animate-pulse" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] text-green-400/80 font-bold uppercase tracking-wider">Yeni Kazanç</span>
                  <span className="text-xs text-white font-medium">{currentWinner.site}'de {currentWinner.amount}</span>
                  <span className="text-[9px] text-gray-400">{currentWinner.user}</span>
                </div>
              </div>
              <button className="bg-white/10 hover:bg-white/20 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg transition-colors ml-2">
                GÖR
              </button>
           </div>
        </div>
      )}

      {/* Popup Modal */}
      {showPopup && config.popup && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
            {/* Kar efekti */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {[...Array(20)].map((_, i) => (
                    <div key={i} className="absolute bg-white rounded-full opacity-50 animate-fall" 
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `-10px`,
                            width: `${Math.random() * 5 + 2}px`,
                            height: `${Math.random() * 5 + 2}px`,
                            animationDuration: `${Math.random() * 3 + 2}s`,
                            animationDelay: `${Math.random() * 2}s`
                        }}
                    ></div>
                ))}
            </div>

            <div className="bg-gradient-to-br from-[#0B1221] to-[#1a2333] border border-cyan-500/30 rounded-3xl p-6 max-w-md w-full relative shadow-[0_0_50px_rgba(6,182,212,0.3)]">
                <button onClick={() => setShowPopup(false)} className="absolute -top-3 -right-3 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors shadow-lg z-10">
                    <X size={20} />
                </button>
                <div className="text-center relative z-10">
                    {config.popup.image && (
                        <div className="relative">
                            <div className="w-full h-40 bg-[#060a12] rounded-2xl mb-4 border border-white/10 shadow-lg flex items-center justify-center p-2">
                                <img src={config.popup.image} alt="Popup" className="w-full h-full object-contain drop-shadow-lg" />
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1221] to-transparent opacity-30 rounded-2xl pointer-events-none"></div>
                        </div>
                    )}
                    <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-cyan-200 mb-2 drop-shadow-md">{config.popup.title}</h3>
                    
                    {/* Popup Sites List */}
                    {popupSites.length > 0 && (
                        <div className="my-4 space-y-2 max-h-48 overflow-y-auto pr-1 custom-scrollbar">
                            {popupSites.map(site => (
                                <a 
                                    key={site.id} 
                                    href={site.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 p-3 bg-white/5 hover:bg-white/10 rounded-xl transition-all group border border-white/5 hover:border-cyan-500/30"
                                >
                                    <img src={site.image} alt={site.name} className="w-10 h-10 object-contain bg-black/20 rounded-lg p-1" />
                                    <div className="text-left flex-1">
                                        <div className="font-bold text-white text-sm group-hover:text-cyan-300 transition-colors">{site.name}</div>
                                        <div className="text-[10px] text-gray-400">Hemen Kazanmaya Başla!</div>
                                    </div>
                                    <ChevronRight size={16} className="text-gray-500 group-hover:text-white transition-colors" />
                                </a>
                            ))}
                        </div>
                    )}

                    <a 
                        href={config.popup.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-bold py-3.5 rounded-xl mt-4 hover:from-blue-500 hover:to-cyan-500 transition-all shadow-lg shadow-cyan-500/20 text-sm tracking-wide uppercase"
                    >
                        {config.popup.buttonText || "HEMEN KATIL"}
                    </a>
                </div>
            </div>
        </div>
      )}

    </div>
  );
};

export default Home;
